/**
 * IB-701 — presentation + normalisation helpers for the Approvals register.
 *
 * The register unifies three record kinds that live in two existing feeds:
 *   - PRs  — purchase-requisitions-data (status is a string, e.g. "PR - Open").
 *   - EAs  — purchase-orders WITH a procurement_policy_id (record_type = 'ea').
 *   - DCs  — purchase-orders with a null procurement_policy_id (record_type = 'dc').
 * The authoritative EA/DC rule (PurchaseService.php:70) is applied server-side and surfaced as
 * `record_type`; this module only normalises status + shape for display.
 *
 * Colours come from tailwind.config.js; no new tokens are introduced.
 */

import { format } from 'date-fns';

/**
 * Normalise a "raised" date for display so PR rows (raw ISO `opened_at`) and PO rows (a `d-m-Y`
 * string from the PO resource) render identically as `dd-MM-yyyy`. An already-formatted `d-m-Y`
 * value is passed through unchanged; an unparseable value falls back to the raw string.
 */
export const toDisplayDate = (value) => {
    if (!value) {
        return null;
    }

    if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
        return value;
    }

    const parsed = new Date(value);

    return isNaN(parsed.getTime()) ? value : format(parsed, 'dd-MM-yyyy');
};

export const RECORD_TYPES = { PR: 'PR', EA: 'EA', DC: 'DC' };

/** A record's own code, or `{TYPE}-{id}` when it has none yet. */
export const recordReference = (type, code, id) => code || `${type}-${id}`;

export const APPROVAL_STATUS = { PENDING: 'pending', APPROVED: 'approved', REJECTED: 'rejected' };

/** Type-tag pill styling — PR = blue, EA = green, DC = charcoal (per the mockup). */
export const typeTagClass = (type) => {
    switch (type) {
        case RECORD_TYPES.PR:
            return 'tw-bg-blue-15 tw-text-blue-75';
        case RECORD_TYPES.EA:
            return 'tw-bg-green-25 tw-text-charcoal-75';
        case RECORD_TYPES.DC:
        default:
            return 'tw-bg-charcoal-15 tw-text-charcoal-75';
    }
};

/**
 * Map a raw PR status string to the unified pending/approved/rejected bucket. Open PRs are
 * awaiting sign-off (pending); cancelled PRs are treated as rejected for the register's coarse
 * status filter (they are not "approved" and no longer "pending").
 */
export const normalisePrStatus = (status) => {
    switch (status) {
        case 'PR - Approved':
            return APPROVAL_STATUS.APPROVED;
        case 'PR - Rejected':
        case 'PR - Cancelled':
            return APPROVAL_STATUS.REJECTED;
        case 'PR - Open':
        default:
            return APPROVAL_STATUS.PENDING;
    }
};

/**
 * Map a raw PO status integer to the unified bucket. Mirrors PurchaseOrder::MAPPED_STATUSES
 * (0 = pending/open, 1 = approved, 2 = rejected, 3 = cancelled → treated as rejected).
 */
export const normalisePoStatus = (status) => {
    switch (Number(status)) {
        case 1:
            return APPROVAL_STATUS.APPROVED;
        case 2:
        case 3:
            return APPROVAL_STATUS.REJECTED;
        case 0:
        default:
            return APPROVAL_STATUS.PENDING;
    }
};

/** Status pill/dot styling by unified bucket (grey dot / green pill / red pill per the mockup). */
export const statusBadge = (status) => {
    switch (status) {
        case APPROVAL_STATUS.APPROVED:
            return { label: $t('Approved'), pill: 'tw-bg-green-25 tw-text-charcoal-75', dot: 'tw-bg-green', showDot: false };
        case APPROVAL_STATUS.REJECTED:
            return { label: $t('Rejected'), pill: 'tw-bg-red-25 tw-text-red', dot: 'tw-bg-red', showDot: false };
        case APPROVAL_STATUS.PENDING:
        default:
            return { label: $t('Pending'), pill: 'tw-bg-charcoal-10 tw-text-charcoal-75', dot: 'tw-bg-charcoal-25', showDot: true };
    }
};

/** Normalise a PR feed row into the register's unified row shape. */
export const normalisePrRow = (pr) => ({
    key: `pr-${pr.id}`,
    id: pr.id,
    type: RECORD_TYPES.PR,
    reference: recordReference(RECORD_TYPES.PR, pr.pr_code, pr.id),
    procurement: pr.title || '—',
    department: pr.department_name || null,
    value: Number(pr.estimated_value) || 0,
    requestor: pr.opener_name || pr.owner_name || null,
    raised: toDisplayDate(pr.opened_at),
    status: normalisePrStatus(pr.status),
    detailUrl: pr.detailedUrl || null,
});

/**
 * Normalise the PR sitting behind a PO (IB-701, PurchaseOrdersGeneralResource.purchase_requisition)
 * into a compact register-shaped row for the EA inline reveal. Returns null when no PR is linked.
 */
const normaliseLinkedPr = (pr, poId) => {
    if (!pr) {
        return null;
    }

    return {
        key: `po-${poId}-pr-${pr.id}`,
        id: pr.id,
        type: RECORD_TYPES.PR,
        reference: recordReference(RECORD_TYPES.PR, pr.pr_code, pr.id),
        procurement: '—',
        department: pr.department_name || null,
        value: Number(pr.estimated_value) || 0,
        requestor: null,
        raised: toDisplayDate(pr.opened_at),
        status: normalisePrStatus(pr.status),
        detailUrl: pr.detailedUrl || null,
    };
};

/** Normalise a PO feed row into the register's unified row shape (EA or DC by record_type). */
export const normalisePoRow = (po) => ({
    key: `po-${po.id}`,
    id: po.id,
    type: po.record_type === 'ea' ? RECORD_TYPES.EA : RECORD_TYPES.DC,
    reference: recordReference(po.record_type === 'ea' ? RECORD_TYPES.EA : RECORD_TYPES.DC, po.code, po.id),
    procurement: po.procurement_plan_item_name || '—',
    department: po.department_name || null,
    value: Number(po.final_price) || 0,
    requestor: po.requestor_name || po.owner_name || null,
    raised: toDisplayDate(po.requested_at),
    status: normalisePoStatus(po.status),
    detailUrl: po.showUrl || po.detailsUrl || null,
    // Whether this commitment came from the reconciliation queue rather than being
    // raised by hand. Derived server-side from the actual-cost link, so a PR row —
    // which has no such link and never reaches this normaliser — is simply absent
    // rather than reported as manually raised.
    raisedFromActual: !!po.raised_from_actual,
    // The PR behind this PO — present for the EA/DC inline reveal (a DC sits behind a PR too).
    purchaseRequisition: normaliseLinkedPr(po.purchase_requisition, po.id),
});

/**
 * Stable sort placing pending rows first (awaiting sign-off is the register's priority), keeping
 * the incoming order within each bucket. Returns a new array.
 */
export const sortPendingFirst = (rows) =>
    [...rows].sort((a, b) => {
        const aPending = a.status === APPROVAL_STATUS.PENDING ? 0 : 1;
        const bPending = b.status === APPROVAL_STATUS.PENDING ? 0 : 1;

        return aPending - bPending;
    });
